import { type ChangeEvent, useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'

import { PktButton, PktDatepicker, PktMessagebox, PktSelect, PktTextinput } from '..'

interface EventLogEntry {
  timestamp: string
  event: string
  data: string
}

function useEventLog() {
  const [log, setLog] = useState<EventLogEntry[]>([])

  const addEntry = useCallback((event: string, data: string) => {
    const timestamp = new Date().toLocaleTimeString('nb-NO', { hour12: false })
    setLog((prev) => [{ timestamp, event, data }, ...prev].slice(0, 20))
  }, [])

  const clear = useCallback(() => setLog([]), [])

  return { log, addEntry, clear }
}

export default function Datepicker() {
  const [singleValue, setSingleValue] = useState('')
  const [values, setValues] = useState(['2024-08-24', '2024-08-12'])
  const [range, setRange] = useState(['2024-10-12', '2024-10-24'])
  const [rangeTest, setRangeTest] = useState('2024-10-12,2024-10-24')

  return (
    <>
      <main className="page-main pkt-container">
        <h1>Datepicker</h1>

        <section className="component">
          <h2>Datepicker and Select and input on a grid row</h2>
          <div className="pkt-grid pkt-grid--gap-medium pkt-grid--cols-3">
            <div className="pkt-cell pkt-cell--span4">
              <PktDatepicker id="datepicker-grid" label="Datepicker" fullwidth />
            </div>
            <div className="pkt-cell pkt-cell--span4">
              <PktTextinput id="textinput-grid" label="Textinput" />
            </div>
            <div className="pkt-cell pkt-cell--span4">
              <PktSelect
                id="select-grid"
                label="Select"
                options={[
                  { value: 'option1', label: 'Option 1' },
                  { value: 'option2', label: 'Option 2' },
                ]}
              />
            </div>
          </div>
        </section>

        <section className="component">
          <h2>Single date picker</h2>
          <PktDatepicker
            id="single-basic"
            label="Velg dato"
            value={singleValue}
            helptext="Velg en dato fra kalenderen"
            withcontrols
            onValueChange={(vals) => {
              setSingleValue(vals[0] ?? '')
            }}
          />
          <br />
          <PktMessagebox>Valgt dato: {singleValue}</PktMessagebox>
          <PktButton onClick={() => setSingleValue('')}>Reset</PktButton>
        </section>

        <section className="component">
          <h2>Single with min/max (defaultValue)</h2>
          <PktDatepicker
            id="single-minmax"
            label="Velg dato (begrenset)"
            min="2024-10-01"
            max="2024-11-15"
            helptext="Bare datoer i oktober og november 2024"
            defaultValue="2024-10-15"
          />
        </section>

        <section className="component">
          <h2>Range picker onChange-test (uten labels)</h2>
          <PktDatepicker
            id="range-basic"
            label="Velg tidsrom"
            range
            value={rangeTest}
            withcontrols
            optionalTag
            max={'2024-11-15'}
            onChange={(e) => setRangeTest(e.target.value)}
          />
          <br />
          <PktMessagebox>Valgt tidsrom: {rangeTest.split(',').join(' – ')}</PktMessagebox>
          <PktButton onClick={() => setRangeTest('')}>Reset</PktButton>
        </section>

        <section className="component">
          <h2>Range picker (med labels)</h2>
          <PktDatepicker
            id="range-labels"
            label="Velg tidsrom"
            range
            showRangeLabels
            onValueChange={(vals) => setRange(vals)}
          />
        </section>

        <section className="component">
          <h2>Multiple picker</h2>
          <PktDatepicker
            id="multi-basic"
            label="Velg flere datoer"
            multiple
            maxlength={5}
            value={values}
            onValueChange={(vals) => setValues(vals)}
          />
          <br />
          <PktMessagebox>Valgte datoer: {values.join(', ')}</PktMessagebox>
          <PktButton onClick={() => setValues([])}>Reset</PktButton>
        </section>

        <section className="component">
          <h2>Error state</h2>
          <PktDatepicker
            id="error-example"
            label="Dato med feil"
            hasError
            errorMessage="Du må velge en gyldig dato"
            range
          />
        </section>

        <section className="component">
          <h2>Disabled</h2>
          <PktDatepicker id="disabled-example" label="Deaktivert datepicker" disabled defaultValue="2024-06-15" />
        </section>

        <section className="component">
          <h2>Dark mode</h2>
          <div data-mode="dark" style={{ padding: '16px', backgroundColor: 'var(--pkt-color-brand-dark-blue-1000)' }}>
            <PktDatepicker
              id="dark-mode-example"
              label="Datepicker i mørk modus"
              defaultValue="2024-06-15,2024-06-18"
              range
            />
          </div>
        </section>

        <section className="component">
          <h2>Full width</h2>
          <PktDatepicker id="fullwidth-single" label="Single (full bredde)" fullwidth />
          <br />
          <PktDatepicker id="fullwidth-range" label="Range (full bredde)" range fullwidth />
          <br />
          <PktDatepicker
            id="fullwidth-range-labels"
            label="Range med labels (full bredde)"
            range
            showRangeLabels
            fullwidth
          />
          <br />
          <PktDatepicker id="fullwidth-multi" label="Multiple (full bredde)" multiple maxlength={5} fullwidth />
        </section>

        <section className="component">
          <h2>Today override (for snapshot-tester)</h2>
          <PktDatepicker
            id="today-override"
            label="Kalender med overstyrt «i dag»"
            today="2024-06-15"
            helptext="«I dag» er satt til 15. juni 2024"
          />
        </section>

        <OnChangeTestSection />
        <OnValueChangeTestSection />

        <section className="component">
          <h2>Form integration</h2>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              const fd = new FormData(e.currentTarget)
              const data = Object.fromEntries(fd.entries())
              alert('Submitted: ' + JSON.stringify(data))
            }}
          >
            <PktDatepicker id="form-single" label="Dato" name="dato" required requiredTag defaultValue="2024-10-15" />
            <br />
            <PktDatepicker
              id="form-range"
              label="Tidsrom"
              name="tidsrom"
              range
              withcontrols
              defaultValue={['2024-10-12', '2024-10-24']}
            />
            <br />
            <PktDatepicker
              id="form-multi"
              label="Flere datoer"
              name="datoer"
              multiple
              maxlength={3}
              min="2024-10-10"
              max="2024-10-28"
              defaultValue={['2024-10-11', '2024-10-15', '2024-10-18']}
            />
            <br />
            <PktButton type="submit">Submit</PktButton>
          </form>
        </section>

        <ReactHookFormTestSection />
      </main>
    </>
  )
}

type RHFFormData = {
  dato: string
  tidsrom: string
  datoer: string
}

function ReactHookFormTestSection() {
  const [submittedData, setSubmittedData] = useState<string>('')
  const { handleSubmit, register, watch } = useForm<RHFFormData>({
    defaultValues: {
      dato: '2024-10-11',
      tidsrom: '2024-10-11,2024-10-18',
      datoer: '2024-10-11,2024-10-15,2024-10-18',
    },
  })

  const currentValues = watch()

  const onSubmit = (data: RHFFormData) => {
    setSubmittedData(JSON.stringify(data, null, 2))
  }

  return (
    <section className="component">
      <h2>React Hook Form</h2>
      <p>Tester at register() fungerer med PktDatepicker i alle tre moduser.</p>

      <form onSubmit={handleSubmit(onSubmit)}>
        <PktDatepicker id="rhf-single" label="Dato (single)" {...register('dato')} min="2024-10-01" max="2024-10-31" />
        <br />
        <PktDatepicker id="rhf-range" label="Tidsrom (range)" range withcontrols {...register('tidsrom')} />
        <br />
        <PktDatepicker
          id="rhf-multi"
          label="Flere datoer (multiple)"
          multiple
          maxlength={5}
          min="2024-10-01"
          max="2024-10-31"
          {...register('datoer')}
        />

        <br />
        <PktButton type="submit">Submit</PktButton>
      </form>

      <div style={{ marginTop: '8px' }}>
        <strong>watch()-verdier:</strong>
        <pre
          style={{
            fontFamily: 'monospace',
            fontSize: '12px',
            background: '#f5f5f5',
            padding: '8px',
            borderRadius: '4px',
          }}
        >
          {JSON.stringify(currentValues, null, 2)}
        </pre>
      </div>

      {submittedData && (
        <PktMessagebox>
          <strong>Submitted:</strong>
          <pre>{submittedData}</pre>
        </PktMessagebox>
      )}
    </section>
  )
}

const logStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: '12px',
  background: '#f5f5f5',
  padding: '8px',
  maxHeight: '300px',
  overflow: 'auto',
  borderRadius: '4px',
}

function EventLog({ log, clear }: { log: EventLogEntry[]; clear: () => void }) {
  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
        <strong>Hendelseslogg ({log.length})</strong>
        <PktButton onClick={clear}>Tøm logg</PktButton>
      </div>
      <div style={logStyle}>
        {log.length === 0 && <em>Ingen hendelser ennå. Velg en dato for å se output.</em>}
        {log.map((entry, i) => (
          <div key={i} style={{ borderBottom: '1px solid #ddd', padding: '4px 0' }}>
            <span style={{ color: '#666' }}>[{entry.timestamp}]</span>{' '}
            <span style={{ color: '#0066cc', fontWeight: 'bold' }}>{entry.event}</span>
            <pre style={{ margin: '2px 0 0', whiteSpace: 'pre-wrap' }}>{entry.data}</pre>
          </div>
        ))}
      </div>
    </>
  )
}

/**
 * Tester onChange-eventen for alle tre moduser.
 * Forventet oppførsel (per docs/specs):
 *   - Typen er React.ChangeEvent<HTMLInputElement> med e.target.value som kommaseparert ISO-streng
 *   - Single: "2024-06-15"
 *   - Range:  "2024-06-15,2024-06-20"
 *   - Multiple: "2024-06-15,2024-06-20,2024-06-25"
 */
function OnChangeTestSection() {
  const { log, addEntry, clear } = useEventLog()

  const handleChange = (label: string) => (e: ChangeEvent<HTMLInputElement>) => {
    addEntry(
      `onChange (${label})`,
      [
        `type:         ${e.type}`,
        `bubbles:      ${e.bubbles}`,
        `target.name:  ${JSON.stringify(e.target.name)}`,
        `target.value: ${JSON.stringify(e.target.value)}`,
      ].join('\n'),
    )
  }

  return (
    <section className="component">
      <h2>onChange output test</h2>
      <p>Velg datoer i feltene under og se at onChange-eventen inneholder forventet data.</p>

      <PktDatepicker
        id="onchange-single"
        label="Single onChange"
        name="onchange-single"
        onChange={handleChange('single')}
      />

      <PktDatepicker
        id="onchange-range"
        label="Range onChange"
        name="onchange-range"
        range
        onChange={handleChange('range')}
      />

      <PktDatepicker
        id="onchange-multi"
        label="Multiple onChange"
        name="onchange-multi"
        multiple
        maxlength={5}
        onChange={handleChange('multiple')}
      />

      <EventLog log={log} clear={clear} />
    </section>
  )
}

/**
 * Tester onValueChange-callbacken for alle tre moduser.
 * Forventet oppførsel (per docs/specs):
 *   - Typen er string[]
 *   - Single: ["2024-06-15"]
 *   - Range:  ["2024-06-15", "2024-06-20"]
 *   - Multiple: ["2024-06-15", "2024-06-20", "2024-06-25"]
 *   - Tømming gir []
 */
function OnValueChangeTestSection() {
  const { log, addEntry, clear } = useEventLog()

  const handleValueChange = (label: string) => (values: string[]) => {
    addEntry(
      `onValueChange (${label})`,
      [
        `type:     ${Array.isArray(values) ? 'string[]' : typeof values}`,
        `length:   ${values.length}`,
        `values:   ${JSON.stringify(values)}`,
        `joined:   ${JSON.stringify(values.join(','))}`,
      ].join('\n'),
    )
  }

  return (
    <section className="component">
      <h2>onValueChange output test</h2>
      <p>Velg datoer i feltene under og se at onValueChange returnerer forventet string[].</p>

      <PktDatepicker
        id="onvaluechange-single"
        label="Single onValueChange"
        name="onvaluechange-single"
        onValueChange={handleValueChange('single')}
      />

      <PktDatepicker
        id="onvaluechange-range"
        label="Range onValueChange"
        name="onvaluechange-range"
        range
        onValueChange={handleValueChange('range')}
      />

      <PktDatepicker
        id="onvaluechange-multi"
        label="Multiple onValueChange"
        name="onvaluechange-multi"
        multiple
        maxlength={5}
        onValueChange={handleValueChange('multiple')}
      />

      <EventLog log={log} clear={clear} />
    </section>
  )
}
